/**
 * Pure rules for calibre lineages (revision lines) — Phase 2 of the design memo
 * "Calibre revisions / lineage — detailed design (approved)", Confluence
 * 2288156674. No Firestore here; tested in calibreLineageLogic.test.ts.
 *
 * The rules the memo fixes, in one place:
 *   - "Current" is EXPLICIT (D3): the curator's `isCurrent` flag, never derived
 *     from an empty `activeUntilYear`. At most one current member per lineage.
 *   - A lineage placeholder (`isLineagePlaceholder`, the bare base such as a
 *     hypothetical "Seiko VK63") does exactly three things: it can never be
 *     current, it is labelled "Revision unspecified", and it is listed in the
 *     Needs Assessment queue. Nothing else changes for it.
 *   - Year fit suppresses the superseded-revision warning (D14): a ref whose
 *     year lies inside the picked revision's years is clear evidence.
 *   - Cross-family members warn, never block (D6).
 */
import { normalizeCalibreKey } from './calibreResolution';

/** The lineage fields a calibre carries (mirrors shared-types CalibreEntry v1.141.0). */
export interface LineageMemberFields {
  id: string;
  name: string;
  calibreLineageId?: string;
  revisionLabel?: string;
  activeFromYear?: number;
  activeUntilYear?: number;
  isCurrent?: boolean;
  isLineagePlaceholder?: boolean;
  familyId?: string | null;
}

export type RevisionChip =
  | { kind: 'current'; label: 'Current' }
  | { kind: 'superseded'; label: string; currentName?: string }
  | { kind: 'placeholder'; label: 'Revision unspecified' }
  | { kind: 'unset'; label: 'Not marked' };

/**
 * The state chip on a lineage member. With the line's `members`, a superseded
 * revision points at its DIRECT successor (A → B, B → C, C → D); without them,
 * or when it has no later revision, at the current one.
 */
export function revisionChip(
  member: LineageMemberFields,
  current: LineageMemberFields | null | undefined,
  members?: readonly LineageMemberFields[],
): RevisionChip {
  if (member.isLineagePlaceholder) return { kind: 'placeholder', label: 'Revision unspecified' };
  if (member.isCurrent) return { kind: 'current', label: 'Current' };
  if (current && current.id !== member.id) {
    const next = (members && directSuccessor(member, members)) || current;
    return { kind: 'superseded', label: `Superseded → ${next.name}`, currentName: current.name };
  }
  // A lineage with no current member yet: the curator has not said, so neither
  // do we — an empty activeUntilYear is NOT a claim of currency (D3).
  return { kind: 'unset', label: 'Not marked' };
}

/** The lineage's current member, or null. More than one is a data error: the first wins, and `currentConflict` says so. */
export function currentMember<T extends LineageMemberFields>(members: readonly T[]): T | null {
  return members.find((m) => m.isCurrent === true && !m.isLineagePlaceholder) ?? null;
}

export function currentConflict(members: readonly LineageMemberFields[]): boolean {
  return members.filter((m) => m.isCurrent === true).length > 1;
}

/**
 * Display (and chronological) order: placeholders first (they head the line),
 * then by activeFromYear ascending, undated last, then by revisionLabel
 * (A < B < D; unlabelled original first), then by name.
 */
export function sortMembers<T extends LineageMemberFields>(members: readonly T[]): T[] {
  return [...members].sort((a, b) => {
    if (!!a.isLineagePlaceholder !== !!b.isLineagePlaceholder) return a.isLineagePlaceholder ? -1 : 1;
    const ay = a.activeFromYear ?? Number.POSITIVE_INFINITY;
    const by = b.activeFromYear ?? Number.POSITIVE_INFINITY;
    if (ay !== by) return ay - by;
    const label = (a.revisionLabel ?? '').localeCompare(b.revisionLabel ?? '', undefined, { numeric: true });
    return label || a.name.localeCompare(b.name);
  });
}

/** The revision that came straight after `member` in its line (placeholders skipped), or null for the latest. */
export function directSuccessor<T extends LineageMemberFields>(member: LineageMemberFields, members: readonly T[]): T | null {
  const ordered = sortMembers(members.filter((m) => !m.isLineagePlaceholder || m.id === member.id));
  const at = ordered.findIndex((m) => m.id === member.id);
  if (at < 0) return null;
  return ordered.slice(at + 1).find((m) => !m.isLineagePlaceholder) ?? null;
}

/** Whether `year` lies in the member's active years. Needs a start year; an open end is "to date". */
export function yearInRevision(member: Pick<LineageMemberFields, 'activeFromYear' | 'activeUntilYear'>, year: number | null | undefined): boolean {
  if (typeof year !== 'number' || !Number.isFinite(year)) return false;
  if (typeof member.activeFromYear !== 'number') return false;
  if (year < member.activeFromYear) return false;
  if (typeof member.activeUntilYear === 'number' && year > member.activeUntilYear) return false;
  return true;
}

export type RevisionPickWarning =
  | { kind: 'superseded'; currentName: string }
  | { kind: 'placeholder'; currentName?: string }
  | { kind: 'noCurrent' };

/**
 * Should picking `picked` for a ref warn? Null = no.
 *
 * Only lineage members warn, only when they are not current, and never when the
 * ref's year fits the picked revision's years. A placeholder warns differently:
 * linking to "revision unspecified" is allowed, but the curator should know a
 * specific revision exists.
 */
export function revisionPickWarning(
  picked: LineageMemberFields,
  current: LineageMemberFields | null | undefined,
  refYear: number | null | undefined,
): RevisionPickWarning | null {
  if (!picked.calibreLineageId) return null;
  if (picked.isCurrent && !picked.isLineagePlaceholder) return null;
  if (picked.isLineagePlaceholder) return { kind: 'placeholder', currentName: current?.name };
  if (yearInRevision(picked, refYear)) return null;
  if (!current) return { kind: 'noCurrent' };
  if (current.id === picked.id) return null;
  return { kind: 'superseded', currentName: current.name };
}

/** The first plausible four-digit year in a value (number or text like "1982-present"). */
export function firstYear(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 1700 && value <= 2200) return value;
  if (typeof value === 'string') {
    const m = value.match(/\b(1[7-9]\d\d|2[01]\d\d)\b/);
    return m ? Number(m[1]) : null;
  }
  return null;
}

/** Integer year from a form input; '' / junk → undefined (cleared). */
export function parseYearInput(raw: string): number | undefined {
  const t = raw.trim();
  if (!/^\d{4}$/.test(t)) return undefined;
  return Number(t);
}

/** Problems with a member's own fields. Warnings only — none block a save. */
export function memberWarnings(
  member: LineageMemberFields,
  siblings: readonly LineageMemberFields[],
): string[] {
  const out: string[] = [];
  if (
    typeof member.activeFromYear === 'number' &&
    typeof member.activeUntilYear === 'number' &&
    member.activeUntilYear < member.activeFromYear
  ) {
    out.push('Active until is before active from.');
  }
  const families = new Set(
    siblings.filter((s) => s.id !== member.id && s.familyId).map((s) => s.familyId as string),
  );
  if (member.familyId && families.size > 0 && !families.has(member.familyId)) {
    out.push('This calibre is in a different movement family from the rest of its revision line.');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Brand-AKA reassignment (memo 5.5, ETA 2824 refinement)
// ---------------------------------------------------------------------------

export interface AliasLike {
  name: string;
  brandId?: string;
}

export interface RefLike {
  brandId: string;
  refId: string;
  /** The calibre text the ref shows — the evidence. */
  calibre: string;
  isVariant?: boolean;
}

export interface BrandAkaGroup {
  brandId: string;
  akaName: string;
  refs: RefLike[];
}

/**
 * Group a calibre's linked refs under its BRAND-SCOPED aliases.
 *
 * A ref joins a group when it belongs to that brand and its calibre text is
 * that alias (normalised). Refs matching only an un-branded alias, or no alias,
 * are not grouped: they stay on this record as the fallback.
 * Groups with no refs are still returned, so the curator sees every brand AKA.
 */
export function groupRefsByBrandAka(aliases: readonly AliasLike[], refs: readonly RefLike[]): BrandAkaGroup[] {
  const groups: BrandAkaGroup[] = [];
  const seen = new Set<string>();
  for (const a of aliases) {
    if (!a.brandId || !a.name.trim()) continue;
    const key = `${a.brandId}|${normalizeCalibreKey(a.name)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const wanted = normalizeCalibreKey(a.name);
    groups.push({
      brandId: a.brandId,
      akaName: a.name,
      refs: refs.filter((r) => r.brandId === a.brandId && normalizeCalibreKey(r.calibre) === wanted),
    });
  }
  return groups.sort((x, y) => x.brandId.localeCompare(y.brandId) || x.akaName.localeCompare(y.akaName));
}

// ---------------------------------------------------------------------------
// Folded-alias split (memo 5.7)
// ---------------------------------------------------------------------------

export interface SplitCandidate {
  /** The alias as stored, e.g. "VK63A". */
  alias: string;
  /** The revision suffix relative to the calibre's name, e.g. "A" or "-2". */
  revisionLabel: string;
}

/**
 * Aliases that read as revisions of this calibre: the calibre's name (or a name
 * whose maker prefix differs) followed by a short suffix — a letter, or a
 * -N / .N / /N revision mark. "VK63" with aliases VK63A..VK63D yields A..D;
 * "2824" with "2824-2" yields "-2". Prose aliases and the bare name are skipped.
 */
export function foldedAliasCandidates(calibreName: string, aliases: readonly string[]): SplitCandidate[] {
  const stem = coreCode(calibreName);
  if (!stem) return [];
  const out: SplitCandidate[] = [];
  const seen = new Set<string>();
  for (const alias of aliases) {
    const code = coreCode(alias);
    if (!code || !code.startsWith(stem) || code === stem) continue;
    const rest = code.slice(stem.length);
    if (!/^([A-Z]|[-./]\d{1,2})$/.test(rest)) continue;
    if (seen.has(rest)) continue;
    seen.add(rest);
    out.push({ alias: alias.trim(), revisionLabel: rest });
  }
  return out.sort((a, b) => a.revisionLabel.localeCompare(b.revisionLabel));
}

/** The movement code in a name: the last whitespace token, uppercased ("Seiko VK63" → "VK63", "ETA 2824-2" → "2824-2"). */
function coreCode(name: string): string {
  const tokens = name.trim().split(/\s+/);
  return (tokens[tokens.length - 1] ?? '').toUpperCase();
}

/** The new calibre's name for a split-out revision: the source's name with its code swapped. */
export function splitRevisionName(calibreName: string, candidate: SplitCandidate): string {
  const tokens = calibreName.trim().split(/\s+/);
  tokens[tokens.length - 1] = `${tokens[tokens.length - 1]}${candidate.revisionLabel}`;
  return tokens.join(' ');
}

/**
 * The bulk-import row notice for a matched calibre (memo D14: inline, per row).
 * Null when the match is not a non-current lineage member, or when EVERY
 * matched entry with a year falls inside the revision's years — at least one
 * year is needed for that evidence.
 */
export function bulkRevisionNotice(
  matched: LineageMemberFields,
  current: LineageMemberFields | null | undefined,
  entryYears: ReadonlyArray<number | null>,
): string | null {
  if (!matched.calibreLineageId) return null;
  if (matched.isCurrent && !matched.isLineagePlaceholder) return null;
  const years = entryYears.filter((y): y is number => typeof y === 'number');
  if (!matched.isLineagePlaceholder && years.length > 0 && years.every((y) => yearInRevision(matched, y))) {
    return null;
  }
  if (matched.isLineagePlaceholder) {
    return `Revision unspecified${current ? ` — the current revision is ${current.name}` : ''}`;
  }
  return `Non-current revision selected${current && current.id !== matched.id ? ` — the current is ${current.name}` : ''}`;
}

// ── Year → revision resolver ────────────────────────────────────────────────
//
// One source of truth for picking a member of a revision line when a ref
// matched the line but did not name a revision (bulk import, and the
// on-demand "Reassign by year" tool). Order (James, 2026-11-03):
//   1. a year the curator gave (typed override, or the ✨ AI lookup);
//   2. the ref's own year (production.releaseYear / startYear);
//   3. the brand's founded year — ONLY when founded ≥ 2000: a heritage brand's
//      founding says nothing about which revision a modern piece uses;
//   4. the default revision: the current one, else the latest activeFromYear,
//      else the lexicographically-latest revisionLabel.
// Placeholders are never picked.

export type RevisionResolveMethod =
  | 'explicit'
  | 'ref_year'
  | 'brand_founded'
  | 'current_fallback'
  | 'manual_override'
  | 'ai_retry';
export type RevisionConfidence = 'high' | 'medium' | 'low';

export interface RevisionResolution<T extends LineageMemberFields = LineageMemberFields> {
  member: T;
  method: RevisionResolveMethod;
  confidence: RevisionConfidence;
  /** The year the pick was made on, when there was one. */
  year?: number;
  /** The year lies inside the picked revision's years. */
  inRange: boolean;
}

/** Below this, a brand's founded year is not used as a revision hint. */
export const BRAND_FOUNDED_FLOOR = 2000;

const CONFIDENCE_RANK: Record<RevisionConfidence, number> = { low: 0, medium: 1, high: 2 };
const weaker = (a: RevisionConfidence, b: RevisionConfidence) => (CONFIDENCE_RANK[a] <= CONFIDENCE_RANK[b] ? a : b);

const pickable = <T extends LineageMemberFields>(members: readonly T[]) => members.filter((m) => !m.isLineagePlaceholder);
const labelCompare = (a: LineageMemberFields, b: LineageMemberFields) =>
  (a.revisionLabel ?? '').localeCompare(b.revisionLabel ?? '', undefined, { numeric: true }) || a.name.localeCompare(b.name);
/** Later = later activeFromYear, then later label. */
const later = <T extends LineageMemberFields>(a: T, b: T): T => {
  const ay = a.activeFromYear ?? Number.NEGATIVE_INFINITY;
  const by = b.activeFromYear ?? Number.NEGATIVE_INFINITY;
  if (ay !== by) return ay > by ? a : b;
  return labelCompare(a, b) >= 0 ? a : b;
};

/** The line's default revision: the current one, else the latest by activeFromYear, else by revisionLabel. */
export function defaultRevision<T extends LineageMemberFields>(members: readonly T[]): T | null {
  const live = pickable(members);
  if (live.length === 0) return null;
  return currentMember(live) ?? live.reduce((a, b) => later(a, b));
}

/**
 * The revision for a year, or null when no member has an activeFromYear.
 * In range → that revision (the later one if ranges overlap). In a gap → the
 * revision closest BEFORE the year. Before every revision → the earliest.
 * After every revision ended → the default revision.
 */
export function revisionForYear<T extends LineageMemberFields>(
  members: readonly T[],
  year: number,
): { member: T; inRange: boolean } | null {
  const dated = pickable(members).filter((m) => typeof m.activeFromYear === 'number');
  if (dated.length === 0) return null;
  const inRange = dated.filter((m) => yearInRevision(m, year));
  if (inRange.length > 0) return { member: inRange.reduce((a, b) => later(a, b)), inRange: true };
  const before = dated.filter((m) => (m.activeFromYear as number) <= year);
  if (before.length === 0) {
    return { member: dated.reduce((a, b) => (later(a, b) === a ? b : a)), inRange: false };
  }
  if (before.length === dated.length) {
    return { member: defaultRevision(members) ?? before.reduce((a, b) => later(a, b)), inRange: false };
  }
  const endOf = (m: T) => m.activeUntilYear ?? (m.activeFromYear as number);
  const closest = before.reduce((a, b) => (endOf(a) !== endOf(b) ? (endOf(a) > endOf(b) ? a : b) : later(a, b)));
  return { member: closest, inRange: false };
}

/** A year the curator supplied for one ref: typed, or returned by the ✨ lookup. */
export interface RevisionYearOverride {
  year: number;
  method: 'manual_override' | 'ai_retry';
  /** The AI's own confidence in the year (ai_retry only); caps the pick's. */
  confidence?: RevisionConfidence;
}

/**
 * Pick a revision in a line. Null only when the line has no pickable member.
 * Confidence: a year inside the revision → high; a year outside → medium; the
 * brand's founded year or the default → low. An AI year is capped by the AI's
 * own confidence.
 */
export function resolveRevisionInLine<T extends LineageMemberFields>(input: {
  members: readonly T[];
  refYear?: number | null;
  brandFounded?: number | null;
  override?: RevisionYearOverride | null;
}): RevisionResolution<T> | null {
  const { members, refYear, brandFounded, override } = input;
  const byYear = (year: number, method: RevisionResolveMethod, cap?: RevisionConfidence): RevisionResolution<T> | null => {
    const hit = revisionForYear(members, year);
    if (!hit) return null;
    const fit: RevisionConfidence = method === 'brand_founded' ? 'low' : hit.inRange ? 'high' : 'medium';
    return { member: hit.member, method, confidence: cap ? weaker(fit, cap) : fit, year, inRange: hit.inRange };
  };
  const steps: Array<() => RevisionResolution<T> | null> = [
    () => (override ? byYear(override.year, override.method, override.confidence) : null),
    () => (typeof refYear === 'number' ? byYear(refYear, 'ref_year') : null),
    () =>
      typeof brandFounded === 'number' && brandFounded >= BRAND_FOUNDED_FLOOR
        ? byYear(brandFounded, 'brand_founded')
        : null,
  ];
  for (const step of steps) {
    const r = step();
    if (r) return r;
  }
  const member = defaultRevision(members);
  return member ? { member, method: 'current_fallback', confidence: 'low', inRange: false } : null;
}

/**
 * Did the matched string name the matched member's revision itself ("ETA
 * 2824-2", "6R15C")? Then the match stands and no year is consulted. A bare
 * stem ("ETA 2824"), an unlabelled member or a placeholder → false.
 */
export function namesExplicitRevision(text: string, matched: LineageMemberFields): boolean {
  if (matched.isLineagePlaceholder) return false;
  const label = (matched.revisionLabel ?? '').trim();
  if (!label) return false;
  const tokens = text.trim().split(/\s+/);
  const token = [...tokens].reverse().find((t) => /\d/.test(t)) ?? '';
  return token.toLowerCase().endsWith(label.toLowerCase());
}

/** A ref's year: production.releaseYear, then production.startYear, then the flat scraped fields. */
export function refYearOf(ref: Record<string, unknown>): number | null {
  const production = (ref.production ?? {}) as Record<string, unknown>;
  return (
    firstYear(production.releaseYear) ??
    firstYear(production.startYear) ??
    firstYear(ref.releaseYear) ??
    firstYear(ref.productionYears) ??
    firstYear(ref.startYear) ??
    firstYear(ref.year)
  );
}

/** The badge line for a resolution ("Picked 2824-2 via brand founded ≈ 2024"). */
export function revisionPickReason(r: RevisionResolution): string {
  const name = r.member.name;
  switch (r.method) {
    case 'explicit':
      return `${name} — named in the import`;
    case 'ref_year':
      return `Picked ${name} via ref release year ${r.year}${r.inRange ? '' : ' (outside its years)'}`;
    case 'brand_founded':
      return `Picked ${name} via brand founded ≈ ${r.year}`;
    case 'manual_override':
      return r.year !== undefined ? `Picked ${name} via year ${r.year} (set by hand)` : `${name} — set by hand`;
    case 'ai_retry':
      return `Picked ${name} via AI year ${r.year} (${r.confidence})`;
    case 'current_fallback':
    default:
      return `Defaulted to ${r.member.isCurrent ? 'current' : 'latest'} ${name} — no year`;
  }
}
